import axios from "axios";
import { jsPDF } from "jspdf";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf";
import { getBusinessDocumentUploadSignature } from "./customer.service";

export interface CloudinaryUploadResponse {
  publicId: string;
  secureUrl: string;
  publicUrl: string;
  resourceType: string;
  originalFilename: string;
}

const getCloudinaryConfig = () => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;

  return { cloudName };
};

const CLOUDINARY_REQUEST_LIMIT_BYTES = 10 * 1024 * 1024;
const CLOUDINARY_CHUNK_SIZE_BYTES = 6 * 1024 * 1024;
const CLOUDINARY_ACCOUNT_MAX_UPLOAD_MB = Number(
  process.env.REACT_APP_CLOUDINARY_MAX_UPLOAD_MB || 10,
);
const CLOUDINARY_ACCOUNT_MAX_UPLOAD_BYTES =
  CLOUDINARY_ACCOUNT_MAX_UPLOAD_MB * 1024 * 1024;
const PDF_COMPRESSION_MAX_WIDTH = 1200;
const PDF_COMPRESSION_JPEG_QUALITY = 0.65;

GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const getFileExtension = (fileName: string) => {
  const match = fileName.match(/\.[^/.]+$/);
  return match ? match[0].toLowerCase() : "";
};

const mapCloudinaryResponse = (data: any): CloudinaryUploadResponse => ({
  publicId: data.public_id,
  secureUrl: getRawDeliveryUrl(data.secure_url, data),
  publicUrl: getRawDeliveryUrl(data.url, data),
  resourceType: data.resource_type,
  originalFilename: data.original_filename,
});

const getRawDeliveryUrl = (url: string | undefined, data: any) => {
  if (!url || data?.resource_type !== "raw") {
    return url || "";
  }

  const format = data?.format || getFileExtension(data?.original_filename || "");
  const extension = format ? `.${String(format).replace(/^\./, "")}` : "";

  if (!extension) {
    return url;
  }

  const [baseUrl, queryString] = url.split("?");
  const hasExtension = /\.[^/.?]+$/.test(baseUrl);

  if (hasExtension) {
    return url;
  }

  return `${baseUrl}${extension}${queryString ? `?${queryString}` : ""}`;
};

const isPdfFile = (file: File) =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

const compressPdfFile = async (file: File): Promise<File> => {
  if (!isPdfFile(file)) {
    return file;
  }

  const pdf = await getDocument({
    data: await file.arrayBuffer(),
  }).promise;
  let compressedPdf: jsPDF | null = null;

  try {
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = Math.min(1, PDF_COMPRESSION_MAX_WIDTH / baseViewport.width);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (!context) {
        throw new Error("Unable to create PDF compression canvas.");
      }

      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);

      await page.render({
        canvasContext: context,
        viewport,
      }).promise;

      const orientation = canvas.width > canvas.height ? "landscape" : "portrait";
      const pageWidthMm = (canvas.width * 25.4) / 96;
      const pageHeightMm = (canvas.height * 25.4) / 96;
      const imageData = canvas.toDataURL(
        "image/jpeg",
        PDF_COMPRESSION_JPEG_QUALITY,
      );

      if (!compressedPdf) {
        compressedPdf = new jsPDF({
          orientation,
          unit: "mm",
          format: [pageWidthMm, pageHeightMm],
          compress: true,
        });
      } else {
        compressedPdf.addPage([pageWidthMm, pageHeightMm], orientation);
      }

      compressedPdf.addImage(
        imageData,
        "JPEG",
        0,
        0,
        pageWidthMm,
        pageHeightMm,
        undefined,
        "FAST",
      );

      canvas.width = 0;
      canvas.height = 0;
      page.cleanup();
    }
  } finally {
    await pdf.destroy();
  }

  if (!compressedPdf) {
    return file;
  }

  const compressedBlob = compressedPdf.output("blob");
  if (compressedBlob.size >= file.size) {
    return file;
  }

  return new File([compressedBlob], file.name, {
    type: "application/pdf",
    lastModified: file.lastModified,
  });
};

export const uploadDocumentToCloudinary = async (
  file: File,
  folder = "businessDocuments",
  uploadName?: string,
): Promise<CloudinaryUploadResponse> => {
  const uploadFile = await compressPdfFile(file);
  const cloudinaryFileName = uploadName || uploadFile.name;

  if (uploadFile.size > CLOUDINARY_ACCOUNT_MAX_UPLOAD_BYTES) {
    throw new Error(
      `${file.name} is ${(uploadFile.size / 1024 / 1024).toFixed(2)}MB after compression. Your Cloudinary account currently allows ${CLOUDINARY_ACCOUNT_MAX_UPLOAD_MB}MB uploads.`,
    );
  }

  const config = getCloudinaryConfig();
  const signatureData = await getBusinessDocumentUploadSignature(
    folder,
    cloudinaryFileName,
  );

  if (!signatureData) {
    throw new Error("Failed to create Cloudinary upload signature.");
  }

  const cloudName = signatureData.cloudName || config.cloudName;

  if (!cloudName) {
    throw new Error("Cloudinary cloud name is missing from the upload signature.");
  }

  const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/raw/upload`;

  if (uploadFile.size > CLOUDINARY_REQUEST_LIMIT_BYTES) {
    const uploadId = `${Date.now()}-${uploadFile.name}`;
    let lastResponseData: any = null;

    for (let start = 0; start < uploadFile.size; start += CLOUDINARY_CHUNK_SIZE_BYTES) {
      const end = Math.min(start + CLOUDINARY_CHUNK_SIZE_BYTES, uploadFile.size);
      const formData = new FormData();

      formData.append("file", uploadFile.slice(start, end), cloudinaryFileName);
      formData.append("api_key", signatureData.apiKey);
      formData.append("timestamp", String(signatureData.timestamp));
      formData.append("signature", signatureData.signature);
      formData.append("folder", signatureData.folder);
      if (signatureData.publicId) {
        formData.append("public_id", signatureData.publicId);
      }
      if (signatureData.filenameOverride) {
        formData.append("filename_override", signatureData.filenameOverride);
        formData.append("use_filename", "true");
      }

      const response = await axios.post(uploadUrl, formData, {
        headers: {
          "Content-Range": `bytes ${start}-${end - 1}/${uploadFile.size}`,
          "X-Unique-Upload-Id": uploadId,
        },
      });

      lastResponseData = response.data;
    }

    if (!lastResponseData?.secure_url) {
      throw new Error("Cloudinary chunked upload did not return a file URL.");
    }

    return mapCloudinaryResponse(lastResponseData);
  }

  const formData = new FormData();

  formData.append("file", uploadFile, cloudinaryFileName);
  formData.append("api_key", signatureData.apiKey);
  formData.append("timestamp", String(signatureData.timestamp));
  formData.append("signature", signatureData.signature);
  formData.append("folder", signatureData.folder);
  if (signatureData.publicId) {
    formData.append("public_id", signatureData.publicId);
  }
  if (signatureData.filenameOverride) {
    formData.append("filename_override", signatureData.filenameOverride);
    formData.append("use_filename", "true");
  }

  const response = await axios.post(uploadUrl, formData);

  return mapCloudinaryResponse(response.data);
};
